import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MAX_ATTEMPTS = 20;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const refreshRef = useRef(refreshUser);
  refreshRef.current = refreshUser;
  const [state, setState] = useState("checking"); // checking | done | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= MAX_ATTEMPTS) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.status === "failed" || data.payment_status === "failed") { setState("error"); return; }
        if (data.payment_status === "paid") {
          await refreshRef.current();
          if (!cancelled) setState("done");
          return;
        }
        // Trials charge $0 today, so the status poll can lag — if the user's
        // subscription is already active, treat it as success.
        const u = await refreshRef.current();
        if (u?.has_app_access) { if (!cancelled) setState("done"); return; }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 relative z-10">
      <div className="max-w-md w-full text-center bg-[#121212] border border-white/10 p-10 fade-up">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-[#007AFF] mx-auto mb-6" size={48} />
            <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-2">Confirming your trial</h1>
            <p className="text-white/50">Hang tight while we activate your subscription...</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="text-[#34C759] mx-auto mb-6" size={48} />
            <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-2">You're in!</h1>
            <p className="text-white/50 mb-8">Your 7-day free trial is active. Time to train.</p>
            <button data-testid="go-to-dashboard" onClick={() => navigate("/dashboard")} className="w-full bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide px-6 py-3.5 transition-transform duration-150 hover:-translate-y-0.5">
              Go to Dashboard
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="text-[#FF9500] mx-auto mb-6" size={48} />
            <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-2">Still processing</h1>
            <p className="text-white/50 mb-8">This is taking longer than expected. Your subscription may still activate shortly.</p>
            <button onClick={() => navigate("/dashboard")} className="w-full border border-white/20 hover:border-white/50 text-white font-bold uppercase tracking-wide px-6 py-3.5">
              Continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}
